/**
 * Dashboard API client helper.
 *
 * Provides typed methods for calling platform JSON API handlers.
 * All requests go to /api/ (platform), /api/my/ (app), or /api/p/:ns/ (plugin).
 *
 * Usage:
 *   DashboardAPI.list("platform.plugins", filters, page, sort, limit)
 *   DashboardAPI.withBase("/api/my/app.site-content").list(filters, page, sort, limit)
 */

async function request(url, payload) {
  var response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {})
  });

  var body = null;
  try {
    body = await response.json();
  } catch (error) {
    var parseError = new Error("Dashboard API response is not valid JSON.");
    parseError.originalError = error;
    parseError.status = response.status;
    parseError.url = url;
    throw parseError;
  }

  if (!response.ok || !(body && body.success)) {
    var serverError = (((body || {}).errors || [])[0]) || ((body || {}).error) || null;
    if (serverError && typeof serverError === "object") {
      var message = serverError.description || serverError.title || "Dashboard API request failed.";
      var errWithNamespace = new Error(message);
      Object.assign(errWithNamespace, serverError);
      errWithNamespace.response = body;
      errWithNamespace.status = response.status;
      errWithNamespace.url = url;
      throw errWithNamespace;
    }

    var err = new Error("Dashboard API request failed.");
    err.response = body;
    err.status = response.status;
    err.url = url;
    throw err;
  }

  return body;
}

/**
 * Build an API base URL from a page namespace.
 * Uses /api/<namespace> (platform default).
 * Pass an explicit apiUrl to override (e.g. "/api/my/app.site-content").
 */
function buildBase(pageNamespace, action, apiUrl) {
  var base = apiUrl
    ? apiUrl.replace(/\/$/, "")
    : "/api/" + encodeURIComponent(pageNamespace);
  return base + "/" + action;
}

/**
 * A scoped DashboardAPI bound to a specific API base URL.
 * Returned by DashboardAPI.withBase(apiUrl).
 */
function createScoped(apiUrl) {
  return {
    list: async function(filters, page, sort, limit) {
      return await request(apiUrl.replace(/\/$/, "") + "/list", {
        filters: filters || {},
        page:    page    || 1,
        sort:    sort    || {},
        limit:   limit   || 25
      });
    },
    get: async function(id) {
      return await request(apiUrl.replace(/\/$/, "") + "/get", { id: id });
    },
    save: async function(id, data) {
      return await request(apiUrl.replace(/\/$/, "") + "/save", { id: id, data: data || {} });
    },
    delete: async function(id) {
      return await request(apiUrl.replace(/\/$/, "") + "/delete", { id: id });
    },
    action: async function(actionName, data) {
      return await request(apiUrl.replace(/\/$/, "") + "/" + actionName, { action: actionName, data: data || {} });
    }
  };
}

var DashboardAPI = {
  /**
   * Returns a scoped API instance bound to apiUrl.
   * @param {string} apiUrl  Full API base, e.g. "/api/my/app.site-content"
   */
  withBase: function(apiUrl) {
    return createScoped(apiUrl);
  },

  list: async function(pageNamespace, filters, page, sort, limit, apiUrl) {
    return await request(buildBase(pageNamespace, "list", apiUrl), {
      filters: filters || {},
      page:    page    || 1,
      sort:    sort    || {},
      limit:   limit   || 25
    });
  },
  get: async function(pageNamespace, id, apiUrl) {
    return await request(buildBase(pageNamespace, "get", apiUrl), { id: id });
  },
  save: async function(pageNamespace, id, data, apiUrl) {
    return await request(buildBase(pageNamespace, "save", apiUrl), { id: id, data: data || {} });
  },
  delete: async function(pageNamespace, id, apiUrl) {
    return await request(buildBase(pageNamespace, "delete", apiUrl), { id: id });
  },
  action: async function(pageNamespace, actionName, data, apiUrl) {
    return await request(buildBase(pageNamespace, actionName, apiUrl), { action: actionName, data: data || {} });
  }
};

module.exports = DashboardAPI;
